import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../lib/api";
import PublicShell from "../../components/PublicShell";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { Camera, CheckCircle2, XCircle, Upload } from "lucide-react";

const ANSWER_OPTS = [
  { v: "yes", l: "Sim", cls: "bg-emerald-50 border-emerald-300 text-emerald-800" },
  { v: "no", l: "Não", cls: "bg-red-50 border-red-300 text-red-800" },
  { v: "na", l: "Não se aplica", cls: "bg-slate-50 border-slate-300 text-slate-700" },
];

export default function ChecklistFill() {
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [observation, setObservation] = useState("");
  const [leftObject, setLeftObject] = useState("");
  const [name, setName] = useState("");
  const [cpf, setCpf] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/checklist/${token}`);
        setData(r.data);
        const map = {};
        (r.data.answers || []).forEach((a) => { map[a.question_id] = a; });
        setAnswers(map);
        setObservation(r.data.observation || "");
        setName(r.data.reservation?.guest_name || "");
        setState(r.data.already_signed ? "done" : "valid");
      } catch { setState("invalid"); }
    })();
  }, [token]);

  const setAns = (qid, patch) => setAnswers({ ...answers, [qid]: { ...(answers[qid] || { question_id: qid }), ...patch } });

  async function uploadFile(qid, file, forObservation = false) {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", file.type.startsWith("video/") ? "video" : "photo");
    try {
      const r = await api.post(`/public/checklist/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (forObservation) {
        // stash in state for the sign call
        setData({ ...data, observation_attachments: [...(data.observation_attachments || []), r.data] });
      } else {
        const cur = answers[qid] || { question_id: qid, value: "no" };
        setAns(qid, { attachments: [...(cur.attachments || []), r.data] });
      }
      toast.success("Anexo enviado");
    } catch (e) { toast.error("Falha no upload"); }
  }

  async function sign(e) {
    e.preventDefault();
    if (!agreed) { toast.error("Marque a declaração para prosseguir"); return; }
    if (!name.trim() || cpf.replace(/\D/g, "").length < 11) { toast.error("Nome completo e CPF são obrigatórios"); return; }
    const answersList = data.questions.map((q) => answers[q.id] || { question_id: q.id, value: "na" });
    const missing = answersList.filter((a) => !["yes", "no", "na"].includes(a.value));
    if (missing.length > 0) { toast.error("Responda todas as perguntas antes de assinar"); return; }
    setSaving(true);
    try {
      await api.post(`/public/checklist/${token}/sign`, {
        accepted_name: name.trim(), accepted_cpf: cpf,
        answers: answersList, observation,
        observation_attachments: data.observation_attachments || [],
        guest_left_object: data.kind === "checkout" ? (leftObject || null) : null,
      });
      setState("done");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao registrar assinatura"); }
    finally { setSaving(false); }
  }

  return (
    <PublicShell>
      <section className="max-w-3xl mx-auto px-4 lg:px-8 py-10">
        {state === "loading" && <p className="text-sm text-slate-500">Carregando…</p>}
        {state === "invalid" && (
          <div className="bg-white border border-red-200 rounded p-8" data-testid="checklist-invalid">
            <div className="flex items-center gap-2 text-red-600"><XCircle className="w-5 h-5" /><h1 className="text-xl font-semibold">Link inválido ou expirado</h1></div>
            <p className="text-sm text-slate-600 mt-3">Entre em contato com a Azuhomes pelo WhatsApp para receber um novo link.</p>
          </div>
        )}
        {state === "done" && (
          <div className="bg-white border border-emerald-200 rounded p-8" data-testid="checklist-done">
            <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="w-6 h-6" /><h1 className="text-2xl font-semibold">Checklist registrado</h1></div>
            <p className="text-sm text-slate-600 mt-4">Enviamos uma cópia em PDF para o seu e-mail. Guarde-a para seus arquivos.</p>
          </div>
        )}
        {state === "valid" && data && (
          <>
            <span className="text-[11px] uppercase tracking-[0.28em] text-[#3B9FD4]">Azuhomes</span>
            <h1 className="font-display text-3xl text-[#0AA89C] mt-1">Checklist de {data.kind === "checkin" ? "Check-in" : "Check-out"}</h1>
            <div className="mt-4 bg-white border border-slate-200 rounded p-4 text-sm text-slate-700 space-y-1">
              <div><span className="text-slate-500">Reserva:</span> <b>{data.reservation.code}</b></div>
              <div><span className="text-slate-500">Imóvel:</span> {data.house.name} — {data.house.address}</div>
              <div><span className="text-slate-500">Período:</span> {data.reservation.check_in} → {data.reservation.check_out} ({data.reservation.nights} diárias)</div>
              <div><span className="text-slate-500">Locador:</span> {data.institutional.legal_name}</div>
            </div>

            <form onSubmit={sign} className="mt-6 space-y-4" data-testid="checklist-form">
              {data.questions.map((q, idx) => {
                const a = answers[q.id] || {};
                return (
                  <div key={q.id} className="bg-white border border-slate-200 rounded p-4">
                    <div className="text-sm text-[#22302E]"><b>{idx + 1}.</b> {q.text}</div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {ANSWER_OPTS.map((opt) => (
                        <button key={opt.v} type="button"
                          onClick={() => setAns(q.id, { value: opt.v })}
                          className={`px-3 py-1.5 text-xs rounded border ${a.value === opt.v ? opt.cls : "bg-white border-slate-200 text-slate-600"}`}
                          data-testid={`q-${q.id}-${opt.v}`}>
                          {opt.l}
                        </button>
                      ))}
                    </div>
                    {a.value === "no" && (
                      <div className="mt-3 space-y-2">
                        <Textarea rows={2} placeholder="Descreva o que foi encontrado…" value={a.note || ""} onChange={(e) => setAns(q.id, { note: e.target.value })} data-testid={`q-${q.id}-note`} />
                        <label className="inline-flex items-center gap-2 text-xs text-[#0AA89C] cursor-pointer">
                          <Camera className="w-4 h-4" /> Anexar foto/vídeo
                          <input type="file" accept="image/*,video/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(q.id, e.target.files[0])} />
                        </label>
                        {(a.attachments || []).length > 0 && <div className="text-xs text-slate-500">{a.attachments.length} anexo(s) enviado(s)</div>}
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="bg-white border border-slate-200 rounded p-4">
                <Label>Observações adicionais</Label>
                <Textarea rows={3} value={observation} onChange={(e) => setObservation(e.target.value)} placeholder={data.kind === "checkin" ? "Registre neste momento qualquer divergência…" : "Deseja informar alguma ocorrência, dano ou objeto esquecido?"} data-testid="checklist-observation" />
                <label className="mt-2 inline-flex items-center gap-2 text-xs text-[#0AA89C] cursor-pointer">
                  <Upload className="w-4 h-4" /> Anexar foto/vídeo
                  <input type="file" accept="image/*,video/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(null, e.target.files[0], true)} />
                </label>
                {(data.observation_attachments || []).length > 0 && <div className="text-xs text-slate-500 mt-1">{data.observation_attachments.length} anexo(s)</div>}

                {data.kind === "checkout" && (
                  <div className="mt-4">
                    <Label>Deixou algum objeto pessoal no imóvel?</Label>
                    <div className="mt-2 flex gap-2">
                      {[{ v: "yes", l: "Sim" }, { v: "no", l: "Não" }, { v: "unknown", l: "Não sei informar" }].map((o) => (
                        <button key={o.v} type="button" onClick={() => setLeftObject(o.v)}
                          className={`px-3 py-1.5 text-xs rounded border ${leftObject === o.v ? "bg-[#0AA89C] text-white border-[#0AA89C]" : "bg-white border-slate-200 text-slate-600"}`}>
                          {o.l}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white border border-slate-200 rounded p-4">
                <h3 className="text-sm font-semibold text-[#0AA89C] mb-3">Assinatura eletrônica</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Nome completo</Label><Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="ck-name" /></div>
                  <div><Label>CPF</Label><Input value={cpf} onChange={(e) => setCpf(e.target.value)} required placeholder="000.000.000-00" data-testid="ck-cpf" /></div>
                </div>
                <label className="mt-4 flex items-start gap-2 text-sm text-[#22302E]">
                  <Checkbox checked={agreed} onCheckedChange={setAgreed} data-testid="ck-checkbox" />
                  <span>
                    Declaro que realizei a verificação visual do imóvel e que as respostas acima representam
                    as condições observadas no momento do {data.kind === "checkin" ? "check-in" : "check-out"}.
                  </span>
                </label>
                <div className="mt-4 flex justify-end">
                  <Button type="submit" disabled={!agreed || saving} className="bg-[#0AA89C] hover:bg-[#066B63]" data-testid="ck-submit">
                    {saving ? "Registrando…" : "Assinar e enviar"}
                  </Button>
                </div>
                <p className="mt-3 text-xs text-slate-500">Registraremos data/hora, IP, navegador e o hash SHA-256 do documento como evidência do aceite (Lei 14.063/2020).</p>
              </div>
            </form>
          </>
        )}
      </section>
    </PublicShell>
  );
}
